import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { ConfigurationSection } from '@features/settings/pages/StyleComponents';
import { BodyLarge, BodyRegular, BodySmall } from '../../atoms/fonts/Body';
import Colours from '../../atoms/Colours';
import Icon, { IconTypes } from '../../atoms/icon/Icon';
import { Switch } from '../../atoms/Switch';
import { SimpleSpinner } from '../../atoms/SimpleSpinner';
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton';
import { ButtonSize } from '../../atoms/buttons/ButtonStyling';
import { SendSampleReportButton } from './SendSampleReportButton';
import { AbsenceReportEditor } from './AbsenceReportEditor';
import { TIMEZONES } from '@shared/utils/Timezones';
import { selectAllDepartments } from '@features/whereabouts/pages/departments/data/departments.duck';
import { Department } from '@features/whereabouts/pages/departments/data/departments.models';
import { AbsenceReportFrequency, ReportType, ScheduledAbsenceReport } from '@shared/models/report.models';
import {
  createAbsenceReport,
  deleteAbsenceReport,
  getAbsenceReports,
  sendAbsenceReportSample,
  updateAbsenceReport
} from '@features/settings/services/ReportService';

const MAX_NAMES_SHOWN = 3;

function newReport(): ScheduledAbsenceReport {
  let timezoneId = 'Europe/London';
  try {
    const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TIMEZONES.some(zone => zone.id === browserZone)) {
      timezoneId = browserZone;
    }
  } catch (e) {
    // keep the default
  }
  return {
    name: '',
    enabled: true,
    frequency: AbsenceReportFrequency.DAILY,
    sendDay: 1,
    sendTime: '07:00',
    timezoneId,
    departmentIds: [],
    recipientUserIds: [],
    recipients: [],
  };
}

/** The server returns validation problems as a JSON array of messages. */
function errorText(err: any, fallback: string): string {
  try {
    const parsed = JSON.parse(err?.message);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed.join(' ');
    if (parsed?.message) return parsed.message;
  } catch (e) {
    // not JSON
  }
  return fallback;
}

/**
 * Saved "who is not in work" reports: each one emails chosen people a list of everyone in some
 * departments who is on leave, off sick, training or otherwise away, daily, weekly or monthly.
 * A company can keep as many as it needs, unlike the one-per-type reports above.
 *
 * A saved report is edited in place of its card; a new one is created at the bottom of the list.
 */
export function AbsenceReportsSetting() {
  const { t } = useTranslation();
  const departments: Department[] = useSelector(selectAllDepartments);
  const [reports, setReports] = useState<ScheduledAbsenceReport[] | undefined>(undefined);
  const [editing, setEditing] = useState<ScheduledAbsenceReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Name lists ("Departments", "Sent to") the admin has expanded past the first few names.
  const [expandedLists, setExpandedLists] = useState<Set<string>>(new Set());
  // Bumped when a pause/resume fails, so the switch snaps back to the saved state.
  const [switchResets, setSwitchResets] = useState(0);

  useEffect(() => {
    let active = true;
    getAbsenceReports()
      .then(result => { if (active) setReports(result); })
      .catch(() => { if (active) setReports([]); });
    return () => { active = false; };
  }, []);

  const departmentNames = useMemo(() => {
    const names = new Map<number, string>();
    departments.forEach(department => names.set(department.id, department.name));
    return names;
  }, [departments]);

  const storeSaved = (saved: ScheduledAbsenceReport) => {
    setReports(prev => {
      const others = (prev ?? []).filter(existing => existing.id !== saved.id);
      return [...others, saved].sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  const startEditing = (report: ScheduledAbsenceReport) => {
    setError(null);
    setEditing(report);
  };

  const stopEditing = () => {
    setEditing(null);
    setError(null);
  };

  const save = async (report: ScheduledAbsenceReport) => {
    setError(null);
    setBusy(true);
    try {
      storeSaved(report.id ? await updateAbsenceReport(report) : await createAbsenceReport(report));
      setEditing(null);
    } catch (err: any) {
      setError(errorText(err, t('settings.absence-reports.save-failed')));
    } finally {
      setBusy(false);
    }
  };

  const setEnabled = async (report: ScheduledAbsenceReport, enabled: boolean) => {
    if (busy || editing) {
      setSwitchResets(count => count + 1);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      storeSaved(await updateAbsenceReport({ ...report, enabled }));
    } catch (err: any) {
      setError(errorText(err, t('settings.absence-reports.save-failed')));
      setSwitchResets(count => count + 1);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (report: ScheduledAbsenceReport) => {
    if (!report.id || !window.confirm(t('settings.absence-reports.delete-confirm', { name: report.name, interpolation: { escapeValue: false } }))) return;
    setError(null);
    setBusy(true);
    try {
      await deleteAbsenceReport(report.id);
      setReports(prev => (prev ?? []).filter(existing => existing.id !== report.id));
    } catch (err: any) {
      setError(errorText(err, t('settings.absence-reports.delete-failed')));
    } finally {
      setBusy(false);
    }
  };

  const scheduleSummary = (report: ScheduledAbsenceReport) => {
    const time = report.sendTime;
    switch (report.frequency) {
      case AbsenceReportFrequency.DAILY:
        return t('settings.absence-reports.summary-daily', { time });
      case AbsenceReportFrequency.WEEKLY:
        return t('settings.absence-reports.summary-weekly', { day: moment().isoWeekday(report.sendDay).format('dddd'), time });
      default:
        return t('settings.absence-reports.summary-monthly', { time });
    }
  };

  const expandList = (listKey: string) => {
    setExpandedLists(prev => new Set(prev).add(listKey));
  };

  const shortList = (names: string[], listKey: string) => {
    if (names.length === 0) return t('settings.absence-reports.summary-none');
    if (names.length <= MAX_NAMES_SHOWN || expandedLists.has(listKey)) return names.join(', ');
    return (
      <>
        {names.slice(0, MAX_NAMES_SHOWN).join(', ')} {t('settings.absence-reports.summary-and')}{' '}
        <MoreLink type="button" onClick={() => expandList(listKey)}>
          {t('settings.absence-reports.summary-more', { count: names.length - MAX_NAMES_SHOWN })}
        </MoreLink>
      </>
    );
  };

  const editor = (report: ScheduledAbsenceReport) => (
    <AbsenceReportEditor key={report.id ?? 'new'}
                         initial={report}
                         busy={busy}
                         error={error}
                         onSave={save}
                         onCancel={stopEditing} />
  );

  const locked = busy || editing !== null;

  return (
    <ConfigurationSection className={'absenceReports__section'}>
      <BodyLarge colour={Colours.blue} style={{ marginBottom: 4 }}>{t('settings.absence-reports.title')}</BodyLarge>
      <BodyRegular style={{ marginBottom: 16 }}>{t('settings.absence-reports.subtitle')}</BodyRegular>

      {reports === undefined ? (
        <SimpleSpinner size={32} />
      ) : (
        <List>
          {reports.length === 0 && !editing && (
            <BodySmall colour={Colours.darkGrey}>{t('settings.absence-reports.empty')}</BodySmall>
          )}

          {reports.map(report => editing && editing.id === report.id ? editor(editing) : (
            <Card key={report.id}>
              <CardHeader>
                <CardTitle>
                  <BodyRegular weight={600}>{report.name}</BodyRegular>
                  {!report.enabled && <Pill>{t('settings.absence-reports.paused')}</Pill>}
                </CardTitle>
                <Switch key={`${report.id}-${switchResets}`}
                        value={report.enabled}
                        label=""
                        onChange={(enabled: boolean) => setEnabled(report, enabled)} />
              </CardHeader>

              <Details>
                <BodySmall colour={Colours.darkGrey}>{t('settings.absence-reports.label-when')}</BodySmall>
                <BodySmall>{scheduleSummary(report)} ({report.timezoneId})</BodySmall>
                <BodySmall colour={Colours.darkGrey}>{t('settings.absence-reports.label-departments')}</BodySmall>
                <BodySmall>
                  {shortList(report.departmentIds
                    .map(id => departmentNames.get(id))
                    .filter((name): name is string => !!name), `${report.id}-departments`)}
                </BodySmall>
                <BodySmall colour={Colours.darkGrey}>{t('settings.absence-reports.label-recipients')}</BodySmall>
                <BodySmall>
                  {shortList((report.recipients ?? []).map(recipient => `${recipient.firstName} ${recipient.lastName}`), `${report.id}-recipients`)}
                </BodySmall>
              </Details>

              <CardActions>
                <SendSampleReportButton reportType={ReportType.ABSENCE_REPORT}
                                        send={() => sendAbsenceReportSample(report.id as number)} />
                <TextActions>
                  <TextAction type="button" disabled={locked} onClick={() => startEditing(report)}>
                    <Icon icon={IconTypes.Edit} color={'blueIcon'} />
                    {t('settings.absence-reports.edit')}
                  </TextAction>
                  <TextAction type="button" $danger disabled={locked} onClick={() => remove(report)}>
                    <Icon icon={IconTypes.Bin} color={'red'} />
                    {t('settings.absence-reports.delete')}
                  </TextAction>
                </TextActions>
              </CardActions>
            </Card>
          ))}

          {editing && !editing.id && editor(editing)}
        </List>
      )}

      {!editing && (
        <>
          {error && <ErrorBox>{error}</ErrorBox>}
          <div style={{ marginTop: 16 }}>
            <PrimaryButton text={'settings.absence-reports.add'} size={ButtonSize.Small}
                           disabled={reports === undefined || busy} click={() => startEditing(newReport())} />
          </div>
        </>
      )}
    </ConfigurationSection>
  );
}

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  & > div {
    margin-top: 0;
  }
`;

const Card = styled.div`
  padding: 16px;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 8px;
  background-color: ${Colours.white};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  .switch {
    flex-shrink: 0;
  }
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;

  p {
    margin: 0;
    overflow-wrap: anywhere;
  }
`;

const Details = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 4px 16px;
  margin-top: 8px;

  p {
    margin: 0;
    overflow-wrap: anywhere;
  }
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${Colours.lightGrey};

  /* The sample button's own wrapper adds a bottom margin for its use in the sections above */
  & > div:first-child {
    margin-bottom: 0 !important;
  }
`;

const TextActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
`;

const TextAction = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  font-family: soleil, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: ${p => p.$danger ? Colours.darkGrey : Colours.blue};
  cursor: pointer;

  .icon {
    width: 16px;
    height: 16px;
    font-size: 16px;
    justify-content: center;
    color: inherit;
  }

  &:hover:not(:disabled) {
    color: ${p => p.$danger ? Colours.red : Colours.blue};
    background: ${p => p.$danger ? 'rgba(240, 34, 79, 0.08)' : Colours.blue10};
  }
  &:focus-visible {
    outline: 2px solid ${Colours.blue};
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const MoreLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: ${Colours.blue};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    text-decoration: none;
  }
  &:focus-visible {
    outline: 2px solid ${Colours.blue};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const Pill = styled.span`
  font-family: proxima-nova;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${Colours.orange20};
  color: ${Colours.darkGrey};
`;

const ErrorBox = styled.div`
  margin-top: 12px;
  padding: 8px 12px;
  border: 1px solid ${Colours.red};
  color: ${Colours.red};
  border-radius: 4px;
  font-size: 13px;
`;
